import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Checkbox } from '/imports/ui/Checkbox';
import { toastOnError } from '/imports/ui/utils';
import {
  projectManagementPersonalNotificationTypes, ProjectManagementNotificationType,
  projectManagementNotificationChannels, ProjectManagementNotificationChannel,
  projectManagementGetNotificationPreference, projectManagementSaveNotificationPreference,
} from '/imports/api/projectManagement';

const Styled = styled.div`
  margin-top: 2rem;
  h3 { margin-bottom: 0.25rem; }
  .hint { color: ${css.grey3}; font-size: 0.85rem; margin-bottom: 0.75rem; }
  table { border-collapse: collapse; }
  th, td { padding: 0.4rem 1.25rem 0.4rem 0; text-align: left; font-weight: normal; }
  th.ch, td.ch { text-align: center; padding-right: 1.5rem; }
`;

// Exhaustive on purpose: a fallthrough once labelled three different types "Added to a project".
const typeLabel = (t: ProjectManagementNotificationType): string => {
  switch (t) {
    case 'assignment': return strings.NotifTypeAssignment();
    case 'documentApproval': return strings.NotifTypeDocumentApproval();
    case 'documentDecision': return strings.NotifTypeDocumentDecision();
    case 'changeOrderDecision': return strings.NotifTypeChangeOrderDecision();
    case 'stakeholderAdded': return strings.NotifTypeStakeholderAdded();
    case 'invoiceUploaded': return strings.NotifTypeInvoiceUploaded();
    case 'invoiceApproved': return strings.NotifTypeInvoiceApproved();
    case 'message': return strings.NotifTypeMessage();
  }
};
const channelLabel = (c: ProjectManagementNotificationChannel): string => (c === 'inApp' ? strings.InApp() : strings.Email());

/**
 * Per-account toggles for the project-management "directed" notifications (in-app + email per type).
 * Opt-out model: a checked box = the channel is on. Saves immediately on each toggle.
 */
export const ProjectManagementNotificationSettings = () => {
  const [off, setOff] = useState<{ [type: string]: ProjectManagementNotificationChannel[] }>({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    toastOnError(async () => {
      const pref = await projectManagementGetNotificationPreference.call();
      setOff(pref?.off || {});
      setLoaded(true);
    })();
  }, []);
  const enabled = (t: string, c: ProjectManagementNotificationChannel) => !(off[t] || []).includes(c);
  const toggle = (t: string, c: ProjectManagementNotificationChannel) => {
    const current = off[t] || [];
    const next = current.includes(c) ? current.filter((x) => x !== c) : [...current, c];
    const nextOff = { ...off, [t]: next };
    if (!next.length) delete nextOff[t];  // keep the map sparse — absent means fully on
    setOff(nextOff);
    toastOnError(async () => projectManagementSaveNotificationPreference.call({ off: nextOff }))();
  };
  if (!loaded) return null;
  return (
    <Styled>
      <h3>{strings.NotificationSettings()}</h3>
      <table>
        <thead>
          <tr>
            <th></th>
            {projectManagementNotificationChannels.map((c) => <th key={c} className="ch">{channelLabel(c)}</th>)}
          </tr>
        </thead>
        <tbody>
          {projectManagementPersonalNotificationTypes.map((t) => (
            <tr key={t}>
              <td>{typeLabel(t)}</td>
              {projectManagementNotificationChannels.map((c) => (
                <td key={c} className="ch"><Checkbox checked={enabled(t, c)} onChange={() => toggle(t, c)} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Styled>
  );
};
